import type { Ref } from "vue";
import {
  computed,
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  provide,
  ref,
  watch,
} from "vue";

import { cardActions } from "@/action/cardActions";
import { toggleActions } from "@/action/toggleActions";
import { useDragScroll } from "@/components/gestures/useDragScroll";
import { useLongClick } from "@/components/gestures/useLongClick";
import { applyFluidBoardRect } from "@/components/utils/boardRect";
import { useNativeEvents } from "@/composables/useNativeEvents";
import { boardScaleFromWindow } from "@/math/coordinate-transform";
import { calcBoardRect } from "@/math/coordinate-utils";
import { applyCoord, minus } from "@/math/coordinates";
import { longClick } from "@/model/Settings";
import type { Board } from "@/model/board";
import { useBoardStore } from "@/store/board";
import { useBoardSizeStore } from "@/store/boardSize";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useContextMenuStore } from "@/store/contextMenu";
import { useDrawStore } from "@/store/draw";
import { useLinkStore } from "@/store/link";
import { usePanModeStore } from "@/store/panMode";
import { useSelectionStore } from "@/store/selection";
import { useUserStore } from "@/store/user";
import { useZoomStore } from "@/store/zoom";
import { clientCoord } from "@/utils/dom/coordinates";
import { eventTarget, isMiddleClick, isRightClick } from "@/utils/dom/dom";
import { isEmpty } from "@/utils/record";

import { boardKey, boardMetaKey } from "../injectKeys";
import type { BoardContextMenuMode } from "./BoardContextMenu.vue";
import BoardContextMenu from "./BoardContextMenu.vue";
import { useBaseBoard } from "./useBaseBoard";

export function useFluidBoard(board: Ref<Board>, el: Ref<HTMLElement | null>) {
  const levelOfDetails = ref<0 | 1 | 2>(0);
  let fixedFS = 0;
  let rightClickActivatedPanMode = false; // Tracks if right click activated pan mode

  const { active, isLoading } = useBaseBoard(board, applyBoardSize);
  const { addEventListener } = useNativeEvents();
  useLongClick(el, longClick, (e) => {
    if (!useDrawStore().active && !useBoardStore().enlargedStickyNoteId) {
      void cardActions.add("long-press-on-board", { pos: e.pos }, true);
    }
  });
  const { enableDragScroll, disableDragScroll } = useDragScroll(el);

  const boardSize = computed(() => boardScaleFromWindow(useBoardSizeStore().board.size));

  provide(
    boardMetaKey,
    computed(() => ({ size: boardSize.value })),
  );
  provide(boardKey, board);

  onMounted(() => {
    setupElement(el.value!); // in onMounted, the el should never be null
    setupPanModeWatcher();
    // clear all pinned sticky notes when changing the board
    watch(() => useBoardStore().board, useLinkStore().removeAllMarks);
  });

  onActivated(() => {
    window.addEventListener("resize", applyBoardSize);
  });

  onDeactivated(() => {
    window.removeEventListener("resize", applyBoardSize);
  });

  watch(() => [useZoomStore().factor, useBoardSizeStore().scrollableMargin], applyBoardSize, {
    deep: true,
  });

  function applyBoardSize() {
    if (active.value) {
      const newBoardRect = calcBoardRect();
      applyFluidBoardRect(newBoardRect);
      useBoardSizeStore().setBoard(newBoardRect);
    }
  }

  function setupElement(el: HTMLElement) {
    setupLevelOfDetailsCalculator(el);
    registerPointerDown(el);
    setupMagnifyingWatcher(el);
  }

  function registerPointerDown(el: HTMLElement) {
    addEventListener(el, "pointerdown", (e) => {
      const canDrag =
        !useSelectionStore().isSelectingWithRect && (isRightClick(e) || isMiddleClick(e));
      if (!canDrag) {
        return;
      }
      const start = clientCoord(e);
      let dragged = false;

      el.addEventListener("pointermove", onPointerMove);
      el.addEventListener("pointerup", onPointerUp, { once: true });

      function onPointerMove(e: PointerEvent) {
        const dist = applyCoord(minus(clientCoord(e), start), Math.abs);
        if (dist.x > 5 || dist.y > 5) {
          dragged = true;
        }

        if (!usePanModeStore().active) {
          toggleActions.showPanMode("mouse");
          rightClickActivatedPanMode = true;

          // Assume the user is using a mouse if they do a right- or middle-click to pan
          useClientSettingsStore().setEffectiveInputDevice("mouse");
        }
      }

      function onPointerUp(e: PointerEvent) {
        el.removeEventListener("pointermove", onPointerMove);
        el.removeEventListener("pointerup", onPointerUp);

        if (rightClickActivatedPanMode) {
          toggleActions.hidePanMode("internal");
          rightClickActivatedPanMode = false;
        } else if (!dragged) {
          // don't open the context menu when clicking on the sticky note > textarea
          if (eventTarget(e).classList.contains("sticky-note-text-input")) return;

          // Open context menu only if there was **no drag**
          useContextMenuStore().open(BoardContextMenu, {
            position: clientCoord(e),
            mode: "mouse" as BoardContextMenuMode,
          });
        }
      }
    });
  }

  function setupPanModeWatcher() {
    watch(
      () => usePanModeStore().active,
      (isActive) => {
        if (isActive) {
          enableDragScroll(rightClickActivatedPanMode);
        } else {
          disableDragScroll();
        }
      },
      { immediate: true },
    );
  }

  function setupMagnifyingWatcher(el: HTMLElement) {
    watch(
      () => useBoardStore().magnifying,
      (isMagnifying) => {
        if (isMagnifying) {
          el.style.cursor = "zoom-in";
        } else {
          el.style.removeProperty("cursor");
        }
      },
      { immediate: true },
    );
  }

  function setupLevelOfDetailsCalculator(el: HTMLElement) {
    const boardStore = useBoardStore();

    watch(
      () => [
        useZoomStore().zooming,
        isEmpty(board.value.cards),
        boardStore.board?.cardSize.factor,
        // Force full details for screenshots so ALM IDs are visible even when zoomed out
        boardStore.forceFullDetailsForBoardId,
      ],
      async ([isZooming, noCards, , forceFullDetailsForBoardId]) => {
        if (forceFullDetailsForBoardId === board.value.id) {
          levelOfDetails.value = 2;
          return;
        }

        await nextTick();

        if (isZooming || noCards) return;

        const sticky = el.querySelector<HTMLDivElement>(".sticky-note");
        if (!sticky) return;

        const currentSize = sticky.getBoundingClientRect().width;
        if (currentSize > 0) {
          levelOfDetails.value = calcLevelOfDetail(currentSize);
        }
      },
    );
  }

  function calcLevelOfDetail(size: number) {
    if (size <= 50) {
      return 0;
    }
    if (size <= 90) {
      return 1;
    }
    return 2;
  }

  const fixedFontSize = computed(() => {
    if (active.value) {
      fixedFS = 100 / useZoomStore().factor;
    }
    return fixedFS;
  });
  const readonly = computed(() => !useUserStore().isAllowed("edit"));
  const focusAndBlur = { focus: () => el.value?.focus(), blur: () => el.value?.blur() };

  return { active, isLoading, boardSize, fixedFontSize, readonly, levelOfDetails, focusAndBlur };
}
